import { createTheme } from '@mui/material/styles';
import { THEMES } from '../contexts/ThemeContext';

// Dark theme (improved current palette)
export const darkTheme = createTheme({
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    subtitle1: {
      fontSize: 12,
    },
  },
  palette: {
    mode: 'dark',
    primary: {
      main: '#F5CD26',
      light: '#F9D43A',
      dark: '#E6C425',
      contrastText: '#000000',
    },
    secondary: {
      main: '#F9D43A',
      light: '#FCE66F',
      dark: '#E6C425',
      contrastText: 'rgba(0,0,0,0.87)',
    },
    background: {
      default: '#0E0C0C',
      paper: '#1A1617',
      // Slightly-lighter-than-paper surface, for nested cards - matches the
      // exact value the old Colors.backgroundGray/backgroundDark3 constants
      // hardcoded, so this is a zero-visual-change extraction, not a redesign.
      elevated: '#2D2527',
    },
    text: {
      primary: '#FCF9F1',
      secondary: 'rgba(252, 249, 241, 0.7)',
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'background.paper',
          backgroundImage: 'none',
          padding: '10px 0 0 10px',
          color: 'text.primary',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          border: 1,
          textTransform: 'none',
          color: '#FCF9F1',
          borderRadius: "12px",
          fontSize: '.75rem',
        },
        contained: {
          border: 1,
          borderColor: 'white',
          textTransform: 'none',
          color: '#FCF9F1',
          borderRadius: "50rem",
          // background.elevated - one of the theme's three dark levels
          // (default / paper / elevated) rather than a fourth near-black
          backgroundColor: '#2D2527',
          fontSize: '.75rem',
          '&:hover': {
            backgroundColor: '#3A3134',
            color: '#FCF9F1',
          },
        },
        containedPrimary: {
          backgroundColor: '#F5CD26',
          color: '#000000',
          '&:hover': {
            backgroundColor: '#E6C425',
            color: '#000000',
          },
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          backgroundColor: 'transparent', // Default to transparent
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          width: {
            xs: '2.5rem',
            sm: '2.5rem',
            md: '2.5rem',
            lg: '2.5rem',
            xl: '2.5rem',
          },
        },
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: {
          '&.MuiGrid-spacing-xs-1': {
            margin: 0,
            width: '100%',
          },
          '&.MuiGrid-spacing-xs-2': {
            margin: 0,
            width: '100%',
          },
          '&.MuiGrid-spacing-xs-3': {
            margin: 0,
            width: '100%',
          },
        },
        item: {
          '&.MuiGrid-spacing-xs-1': {
            padding: 0,
          },
          '&.MuiGrid-spacing-xs-2': {
            padding: 0,
          },
          '&.MuiGrid-spacing-xs-3': {
            padding: 0,
          },
        },
        container: {
          '&.MuiGrid-spacing-xs-1': {
            margin: 0,
            '& > .MuiGrid-item': {
              padding: 0,
            },
          },
          '&.MuiGrid-spacing-xs-2': {
            margin: 0,
            '& > .MuiGrid-item': {
              padding: 0,
            },
          },
          '&.MuiGrid-spacing-xs-3': {
            margin: 0,
            '& > .MuiGrid-item': {
              padding: 0,
            },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: "none",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {},
      },
    },
    MuiBadge: {
      styleOverrides: {
        colorError: {
          backgroundColor: '#f44336',
          color: '#ffffff',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: 'rgba(255, 255, 255, 0.7)',
          // Match the yellow indicator (sage green is the light theme's accent)
          '&.Mui-selected': {
            color: '#F5CD26',
          },
          '&:hover': {
            color: '#FCF9F1',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: '#F5CD26',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          color: 'text.primary',
          '& .MuiAlert-message': {
            color: 'text.primary',
          },
          '& .MuiAlert-icon': {
            color: 'inherit',
          },
          '&.MuiAlert-standardWarning': {
            color: 'text.primary',
            '& .MuiAlert-message': {
              color: 'text.primary',
            },
          },
          '&.MuiAlert-standardInfo': {
            color: 'text.primary',
            '& .MuiAlert-message': {
              color: 'text.primary',
            },
          },
          '&.MuiAlert-standardError': {
            color: 'text.primary',
            '& .MuiAlert-message': {
              color: 'text.primary',
            },
          },
          '&.MuiAlert-standardSuccess': {
            color: 'text.primary',
            '& .MuiAlert-message': {
              color: 'text.primary',
            },
          },
        },
      },
    },
  },
});

// Light theme
export const lightTheme = createTheme({
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    subtitle1: {
      fontSize: 12,
    },
  },
  palette: {
    mode: 'light',
    primary: {
      main: '#87AE73', // Sage green
      light: '#A8C99F',
      dark: '#6B8E5A',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#9CAF88', // Lighter sage green
      light: '#B8C9A8',
      dark: '#7A8F6A',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#FFFFFF',
      paper: '#F5F5F0', // Light beige/cream for header - contrasts with white background
      // Reuses the theme's own pure white (background.default) as the
      // "elevated" surface - a white card reads as raised against the
      // cream paper tone, with no newly-invented color.
      elevated: '#FFFFFF',
    },
    text: {
      primary: '#1A1617',
      secondary: 'rgba(26, 22, 23, 0.7)',
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'background.paper',
          backgroundImage: 'none',
          padding: '10px 0 0 10px',
          borderBottom: '1px solid',
          borderColor: 'rgba(0, 0, 0, 0.08)', // Light border instead of default divider
          color: 'text.primary',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          border: 1,
          textTransform: 'none',
          color: '#1A1617',
          borderRadius: "12px",
          fontSize: '.75rem',
        },
        contained: {
          border: 1,
          borderColor: '#1A1617',
          textTransform: 'none',
          color: '#1A1617',
          borderRadius: "50rem",
          backgroundColor: '#F5F5F5',
          fontSize: '.75rem',
          '&:hover': {
            backgroundColor: '#E0E0E0',
            color: '#1A1617',
          },
        },
        containedPrimary: {
          backgroundColor: '#87AE73', // Sage green
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: '#6B8E5A', // Darker sage green
            color: '#FFFFFF',
          },
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          backgroundColor: 'transparent', // Default to transparent
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          width: {
            xs: '2.5rem',
            sm: '2.5rem',
            md: '2.5rem',
            lg: '2.5rem',
            xl: '2.5rem',
          },
        },
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: {
          '&.MuiGrid-spacing-xs-1': {
            margin: 0,
            width: '100%',
          },
          '&.MuiGrid-spacing-xs-2': {
            margin: 0,
            width: '100%',
          },
          '&.MuiGrid-spacing-xs-3': {
            margin: 0,
            width: '100%',
          },
        },
        item: {
          '&.MuiGrid-spacing-xs-1': {
            padding: 0,
          },
          '&.MuiGrid-spacing-xs-2': {
            padding: 0,
          },
          '&.MuiGrid-spacing-xs-3': {
            padding: 0,
          },
        },
        container: {
          '&.MuiGrid-spacing-xs-1': {
            margin: 0,
            '& > .MuiGrid-item': {
              padding: 0,
            },
          },
          '&.MuiGrid-spacing-xs-2': {
            margin: 0,
            '& > .MuiGrid-item': {
              padding: 0,
            },
          },
          '&.MuiGrid-spacing-xs-3': {
            margin: 0,
            '& > .MuiGrid-item': {
              padding: 0,
            },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: "none",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {},
      },
    },
    MuiBadge: {
      styleOverrides: {
        colorError: {
          backgroundColor: '#f44336',
          color: '#ffffff',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: 'rgba(26, 22, 23, 0.7)',
          '&.Mui-selected': {
            color: '#87AE73', // Sage green
          },
          '&:hover': {
            color: '#87AE73', // Sage green
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: '#87AE73', // Sage green
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          // Ensure Typography uses theme text colors by default
          color: 'inherit',
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          color: 'inherit',
        },
        secondary: {
          color: 'inherit',
        },
      },
    },
  },
});

// Oasis theme (tan/sand/earth base with light blue accents)
export const naturalTheme = createTheme({
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    subtitle1: {
      fontSize: 12,
    },
  },
  palette: {
    mode: 'dark',
    primary: {
      main: '#87CEEB', // Light blue (sky blue) - accent color
      light: '#B0E0E6', // Powder blue
      dark: '#6BB6FF', // Deeper sky blue
      contrastText: '#1A1617',
    },
    secondary: {
      main: '#87CEEB', // Light blue (sky blue)
      light: '#B0E0E6', // Powder blue
      dark: '#6BB6FF', // Deeper sky blue
      contrastText: '#1A1617',
    },
    background: {
      default: '#8B7355', // Earth tone base
      paper: '#A68B6F', // Lighter earth tone
      // Reuses the palette's own existing lighter-earth accent (already
      // used for button hover below) rather than inventing a new tone.
      elevated: '#D4A574',
    },
    text: {
      primary: '#F5E6D3', // Light sand
      secondary: 'rgba(245, 230, 211, 0.7)',
    },
    // ROYGBP accent colors
    error: {
      main: '#E74C3C', // Red
      light: '#EC7063',
      dark: '#C0392B',
      contrastText: '#FFFFFF', // White for readability
    },
    warning: {
      main: '#F39C12', // Orange
      light: '#F5B041',
      dark: '#D68910',
      contrastText: '#FFFFFF', // White for readability
    },
    info: {
      main: '#3498DB', // Blue (darker - hard to read as text)
      light: '#5DADE2', // Lighter blue (good for text)
      dark: '#2874A6',
      contrastText: '#FFFFFF', // White for readability (use info.light for better contrast)
    },
    success: {
      main: '#27AE60', // Green (hard to read as text)
      light: '#52BE80',
      dark: '#1E8449',
      contrastText: '#FFFFFF', // White for readability
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'background.paper',
          backgroundImage: 'none',
          padding: '10px 0 0 10px',
          color: 'text.primary',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          border: 1,
          textTransform: 'none',
          color: '#F5E6D3',
          borderRadius: "12px",
          fontSize: '.75rem',
        },
        contained: {
          border: 1,
          borderColor: '#D4A574',
          textTransform: 'none',
          color: '#1A1617',
          borderRadius: "50rem",
          backgroundColor: '#A68B6F',
          fontSize: '.75rem',
          '&:hover': {
            backgroundColor: '#D4A574',
            color: '#1A1617',
          },
        },
        containedPrimary: {
          backgroundColor: '#87CEEB', // Light blue
          color: '#1A1617',
          '&:hover': {
            backgroundColor: '#6BB6FF', // Deeper sky blue
            color: '#1A1617',
          },
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          backgroundColor: 'transparent', // Default to transparent
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          width: {
            xs: '2.5rem',
            sm: '2.5rem',
            md: '2.5rem',
            lg: '2.5rem',
            xl: '2.5rem',
          },
        },
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: {
          '&.MuiGrid-spacing-xs-1': {
            margin: 0,
            width: '100%',
          },
          '&.MuiGrid-spacing-xs-2': {
            margin: 0,
            width: '100%',
          },
          '&.MuiGrid-spacing-xs-3': {
            margin: 0,
            width: '100%',
          },
        },
        item: {
          '&.MuiGrid-spacing-xs-1': {
            padding: 0,
          },
          '&.MuiGrid-spacing-xs-2': {
            padding: 0,
          },
          '&.MuiGrid-spacing-xs-3': {
            padding: 0,
          },
        },
        container: {
          '&.MuiGrid-spacing-xs-1': {
            margin: 0,
            '& > .MuiGrid-item': {
              padding: 0,
            },
          },
          '&.MuiGrid-spacing-xs-2': {
            margin: 0,
            '& > .MuiGrid-item': {
              padding: 0,
            },
          },
          '&.MuiGrid-spacing-xs-3': {
            margin: 0,
            '& > .MuiGrid-item': {
              padding: 0,
            },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: "none",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {},
      },
    },
    MuiBadge: {
      styleOverrides: {
        colorError: {
          backgroundColor: '#E74C3C', // Red
          color: '#ffffff',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: 'rgba(245, 230, 211, 0.7)',
          '&.Mui-selected': {
            color: '#87CEEB', // Light blue
          },
          '&:hover': {
            color: '#87CEEB', // Light blue
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: '#87CEEB', // Light blue
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          color: 'text.primary',
          '& .MuiAlert-message': {
            color: 'text.primary',
          },
          '& .MuiAlert-icon': {
            color: 'text.primary',
          },
          '&.MuiAlert-standardWarning': {
            color: 'text.primary',
            '& .MuiAlert-message': {
              color: 'text.primary',
            },
            '& .MuiAlert-icon': {
              color: 'text.primary',
            },
          },
          '&.MuiAlert-standardInfo': {
            color: 'text.primary',
            '& .MuiAlert-message': {
              color: 'text.primary',
            },
            '& .MuiAlert-icon': {
              color: 'text.primary',
            },
          },
          '&.MuiAlert-standardError': {
            color: 'text.primary',
            '& .MuiAlert-message': {
              color: 'text.primary',
            },
            '& .MuiAlert-icon': {
              color: 'text.primary',
            },
          },
          '&.MuiAlert-standardSuccess': {
            color: 'text.primary',
            '& .MuiAlert-message': {
              color: 'text.primary',
            },
            '& .MuiAlert-icon': {
              color: 'text.primary',
            },
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          // Ensure Typography uses theme text colors by default
          color: 'inherit',
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          color: 'inherit',
        },
        secondary: {
          color: 'inherit',
        },
      },
    },
  },
});

export const getTheme = (themeName) => {
  switch (themeName) {
    case THEMES.LIGHT:
      return lightTheme;
    case THEMES.OASIS:
      return naturalTheme; // naturalTheme is now the Oasis theme
    case 'natural': // Legacy support for old theme name
      return naturalTheme;
    case THEMES.DARK:
    default:
      return darkTheme;
  }
};

